import { useNavigate } from 'react-router-dom';
import {
  Building2, Users, FileText, DollarSign, Settings,
  Plus, AlertCircle, CheckCircle, Clock, XCircle,
  ChevronRight, TrendingUp, Activity
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { useAdminDashboard } from '../../hooks/useDashboard';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { stats, recentAgencies, enterpriseCodes, isLoading, isError, error } = useAdminDashboard();

  const getStatusBadge = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full flex items-center gap-1"><CheckCircle className="w-3 h-3" />Active</span>;
      case 'PENDING':
      case 'PENDING_EMAIL_VERIFICATION':
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1"><Clock className="w-3 h-3" />Pending</span>;
      case 'SUSPENDED':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full flex items-center gap-1"><XCircle className="w-3 h-3" />Suspended</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded-full">{status}</span>;
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title="Rentry Admin Dashboard"
        subtitle="Manage agencies, subscriptions, and platform settings"
        icon={<Settings className="w-8 h-8" />}
      />

      {isError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-red-800">{(error as Error)?.message || 'Failed to load dashboard'}</div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Building2 className="w-8 h-8 text-primary/30" />
            <div>
              <p className="text-xs text-gray-500">Total Agencies</p>
              <p className="text-2xl font-bold text-primary">{stats?.totalAgencies || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Activity className="w-8 h-8 text-green-500/30" />
            <div>
              <p className="text-xs text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{stats?.activeAgencies || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-yellow-500/30" />
            <div>
              <p className="text-xs text-gray-500">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats?.pendingAgencies || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-primary/30" />
            <div>
              <p className="text-xs text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-primary">{stats?.totalUsers || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-primary/30" />
            <div>
              <p className="text-xs text-gray-500">Active Leases</p>
              <p className="text-2xl font-bold text-primary">{stats?.totalLeases || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <TrendingUp className="w-8 h-8 text-secondary/30" />
            <div>
              <p className="text-xs text-gray-500">Monthly Revenue</p>
              <p className="text-2xl font-bold text-secondary">${(stats?.monthlyRevenue || 0).toFixed(0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <button
          onClick={() => navigate('/admin/agencies')}
          className="bg-white rounded-lg shadow p-4 flex items-center gap-3 border-2 border-transparent hover:border-secondary transition-colors text-left"
        >
          <Building2 className="w-10 h-10 text-primary p-2 bg-primary/10 rounded-lg" />
          <div>
            <p className="font-semibold text-gray-900">Manage Agencies</p>
            <p className="text-sm text-gray-500">View and manage all agencies</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
        </button>
        <button
          onClick={() => navigate('/admin/enterprise-codes')}
          className="bg-white rounded-lg shadow p-4 flex items-center gap-3 border-2 border-transparent hover:border-secondary transition-colors text-left"
        >
          <DollarSign className="w-10 h-10 text-secondary p-2 bg-secondary/10 rounded-lg" />
          <div>
            <p className="font-semibold text-gray-900">Enterprise Codes</p>
            <p className="text-sm text-gray-500">Manage discount codes</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
        </button>
        <button
          onClick={() => navigate('/admin/subscriptions')}
          className="bg-white rounded-lg shadow p-4 flex items-center gap-3 border-2 border-transparent hover:border-secondary transition-colors text-left"
        >
          <FileText className="w-10 h-10 text-primary p-2 bg-primary/10 rounded-lg" />
          <div>
            <p className="font-semibold text-gray-900">Subscriptions</p>
            <p className="text-sm text-gray-500">Billing and subscriptions</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
        </button>
        <button
          onClick={() => navigate('/admin/settings')}
          className="bg-white rounded-lg shadow p-4 flex items-center gap-3 border-2 border-transparent hover:border-secondary transition-colors text-left"
        >
          <Settings className="w-10 h-10 text-gray-600 p-2 bg-gray-100 rounded-lg" />
          <div>
            <p className="font-semibold text-gray-900">Platform Settings</p>
            <p className="text-sm text-gray-500">Configure platform</p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 ml-auto" />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Agencies */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Agencies</h2>
            <button
              onClick={() => navigate('/admin/agencies')}
              className="text-secondary hover:underline text-sm flex items-center gap-1"
            >
              View All <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {recentAgencies.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No agencies registered yet.
              </div>
            ) : (
              recentAgencies.map(agency => (
                <div
                  key={agency.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/agencies/${agency.id}`)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900">{agency.name}</p>
                      <p className="text-sm text-gray-500">{agency.contactEmail}</p>
                    </div>
                    {getStatusBadge(agency.status)}
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>ABN: {agency.abn}</span>
                    <span>{agency.leaseCount} leases</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Enterprise Codes */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Enterprise Codes</h2>
            <button
              onClick={() => navigate('/admin/enterprise-codes/create')}
              className="flex items-center gap-1 text-sm bg-secondary text-white px-3 py-1.5 rounded-lg hover:bg-secondary/90"
            >
              <Plus className="w-4 h-4" /> Create Code
            </button>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {enterpriseCodes.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No enterprise codes created yet.
              </div>
            ) : (
              enterpriseCodes.map(code => (
                <div key={code.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <p className="font-mono font-medium text-primary">{code.code}</p>
                      <p className="text-sm text-gray-500">{code.description}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      code.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {code.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Used: {code.currentUses}{code.maxUses ? ` / ${code.maxUses}` : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

